import ContentWrapper from "@/components/wrappers/ContentWrapper";
import { replaceUrlPlaceholdersWithUuids, replaceUrlUuidsWithPlaceholders } from "@/utils/navigationUtils";
import { Breadcrumb } from "antd";
import { BreadcrumbItemType, BreadcrumbSeparatorType } from "antd/es/breadcrumb/Breadcrumb";
import { MenuItemType } from "antd/es/menu/interface";
import { Link, useLocation } from "react-router";
import { Permission } from "../../common/security/enums";
import { hasAnyPermission } from "../../common/security/utils";
import { usePermissions } from "../../modules/auth/hooks";
import type { UUID } from "../../typings/global";
import { PagesMap } from "../pages/pagesMap";

export type BreadcrumbsItemType = Partial<BreadcrumbItemType & BreadcrumbSeparatorType>;

const renderBreadcrumb = (path: string, uuids: UUID[], accountPermissions: Permission[]) => {
  const page = PagesMap.get(path);
  if (!page || page.hideBreadcrumbs) {
    return [];
  }

  let crumbs: BreadcrumbsItemType[] = [];
  const outputPath = replaceUrlPlaceholdersWithUuids(path, uuids);

  if (page.parent) {
    crumbs = renderBreadcrumb(page.parent, uuids, accountPermissions);
  }

  const siblings = page.siblings
    ? page.siblings.filter(
        (sibling) => !sibling.permissions || hasAnyPermission(accountPermissions, sibling.permissions),
      )
    : [];

  if (siblings.length > 0) {
    const siblingsItems = siblings.map<MenuItemType>((sibling) => {
      const siblingPage = PagesMap.get(sibling.path);
      const siblingPath = replaceUrlPlaceholdersWithUuids(sibling.path, uuids);
      return {
        key: siblingPath,
        label: <Link to={siblingPath}>{siblingPage?.render || siblingPage?.title}</Link>,
      };
    });
    siblingsItems.unshift({ key: outputPath, label: <Link to={outputPath}>{page.render || page.title}</Link> });

    crumbs.push({
      title: page.render ?? page.title,
      menu: { items: siblingsItems },
    });
  } else {
    crumbs.push({
      title: <Link to={outputPath}>{page.render || page.title}</Link>,
    });
  }

  return crumbs;
};

export const BreadcrumbsBox = () => {
  const { pathname } = useLocation();

  const accountPermissions = usePermissions();

  const currentPage = replaceUrlUuidsWithPlaceholders(pathname);
  const crumbs = renderBreadcrumb(currentPage.path, currentPage.uuids, accountPermissions);

  return (
    <div>
      {PagesMap.get(currentPage.path)?.wrappedStyle ? (
        <ContentWrapper wide={PagesMap.get(currentPage.path)?.wrappedStyle === "wide"}>
          <Breadcrumb items={crumbs} style={{ paddingBottom: 10 }} />
        </ContentWrapper>
      ) : (
        <Breadcrumb items={crumbs} style={{ paddingBottom: 10 }} />
      )}
    </div>
  );
};
